import { MESSAGE } from "../Constants/Message";

const ORG_LABEL_SLUGS = new Set([
  "bipoc-led",
  "powerful-black-led",
  "powerful-brown-led",
  "powerful-indigenous-led",
  "woman-led",
  "lgbtq-led",
  "not-listed",
]);

const ORG_COMMUNITY_SLUGS = new Set([
  "powerful-black-community",
  "powerful-brown-community",
  "powerful-indigenous-community",
  "woman",
  "lgbtq",
  "not-listed",
]);

function mapCheckboxValueToAllowedSlug(
  raw: unknown,
  allowed: Set<string>
): string {
  const trimmed = String(raw ?? "").trim();
  if (allowed.has(trimmed)) return trimmed;
  const slug = trimmed
    .toLowerCase()
    .replace(/\u00a0/g, " ")
    .replace(/\s+/g, "-");
  if (allowed.has(slug)) return slug;
  return trimmed;
}

function toOptionalBoolean(value: unknown): boolean | undefined {
  if (value === true || value === false) return value;
  if (value === "true") return true;
  if (value === "false") return false;
  return undefined;
}

/** RTK Query / fetch error → user-facing message */
export function getApiErrorMessage(error: unknown): string {
  if (!error || typeof error !== "object") {
    return MESSAGE.SYSTEM_ERROR;
  }
  const err = error as {
    data?: { message?: string | string[] };
    error?: string;
    status?: string | number;
  };
  const msg = err.data?.message;
  if (Array.isArray(msg)) return msg.join(". ");
  if (typeof msg === "string" && msg.trim()) return msg.trim();
  if (typeof err.error === "string" && err.error.trim()) return err.error;
  return MESSAGE.SYSTEM_ERROR;
}

/** PUT /user — only fields the API accepts */
export function buildUserUpdatePayload(
  data: Record<string, unknown>,
  options?: { email?: string; includeSparks?: boolean; onboarded?: boolean }
) {
  const location = (data.location ?? {}) as Record<string, unknown>;
  const payload: Record<string, unknown> = {
    name: data.name,
    title: data.title ?? "",
    gender: data.gender,
    pronouns: data.pronouns,
    ethnicity: data.ethnicity,
    location: {
      zip: location.zip ?? "",
      city: location.city ?? "",
      state: location.state ?? "",
      address1: location.address1 ?? "",
      address2: location.address2 ?? "",
      country: location.country ?? "United States",
    },
  };

  if (options?.email) {
    payload.email = options.email;
  }

  if (options?.includeSparks !== false && Array.isArray(data.sparks)) {
    payload.sparks = data.sparks;
  } else if (options?.includeSparks !== false && data.sparks == null) {
    payload.sparks = [];
  }

  if (options?.onboarded === true) {
    payload.onboarded = true;
  }

  return payload;
}

/** Strip read-only / nested fields before organization POST or PUT */
export function sanitizeOrganizationPayload(
  data: Record<string, unknown>
): Record<string, unknown> {
  let body: Record<string, unknown>;
  try {
    body = JSON.parse(JSON.stringify(data)) as Record<string, unknown>;
  } catch {
    body = { ...data };
  }

  const readOnlyKeys = [
    "id",
    "createdAt",
    "updatedAt",
    "goals",
    "status",
    "members",
    "followerCount",
    "followingCount",
    "labelNotListed",
    "communityServedNotListed",
    "step",
    "image",
  ];
  for (const key of readOnlyKeys) {
    delete body[key];
  }

  if (typeof body.type === "string") {
    body.type = body.type.toLowerCase();
  }

  if (!Array.isArray(body.communitiesServed) && Array.isArray(body.communities_served)) {
    body.communitiesServed = body.communities_served;
    delete body.communities_served;
  }

  if (Array.isArray(body.communitiesServed)) {
    body.communitiesServed = body.communitiesServed.map((v) =>
      mapCheckboxValueToAllowedSlug(v, ORG_COMMUNITY_SLUGS)
    );
  }

  if (Array.isArray(body.labels)) {
    body.labels = body.labels.map((v) =>
      mapCheckboxValueToAllowedSlug(v, ORG_LABEL_SLUGS)
    );
  }

  const orgFields = body.orgFields as Record<string, unknown> | undefined;
  if (orgFields && typeof orgFields === "object") {
    const certified = toOptionalBoolean(orgFields.careModelCertified);
    if (certified !== undefined) {
      orgFields.careModelCertified = certified;
    }

    const showPhone = toOptionalBoolean(orgFields.showPhone);
    if (showPhone !== undefined) orgFields.showPhone = showPhone;

    const showAddress = toOptionalBoolean(orgFields.showAddress);
    if (showAddress !== undefined) orgFields.showAddress = showAddress;

    if (orgFields.ein != null && orgFields.ein !== "") {
      const einDigits = String(orgFields.ein).replace(/\D/g, "");
      if (einDigits.length === 9) {
        orgFields.ein = Number(einDigits);
      }
    }

    body.orgFields = orgFields;
  }

  if (body.fundBy != null && body.fund_by == null) {
    body.fund_by = body.fundBy;
  }
  delete body.fundBy;

  return body;
}
